import { PlusCircle } from "react-feather";
import React, { useState, useEffect } from 'react';

import { Link } from "react-router-dom";
import { DatePicker, Modal, Table, Popconfirm } from "antd";
import { ArrowLeft } from "lucide-react";
import moment from "moment";
import CreateTrainingProgram from "../../core/modals/training/CreateTrainingProgram";
import { getAllPrograms, deleteProgramById } from "../../service/trainingService";

const TrainingProgramList = () => {
  const [openCreateProgramModal, setOpenCreateProgramModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [searchProgramName, setSearchProgramName] = useState("");
  const [searchProgramDate, setSearchProgramDate] = useState(null);
  const [paginationPrograms, setPaginationPrograms] = useState({ 
    current: 1, 
    pageSize: 5, 
    total: 0 
  });
  const [loading, setLoading] = useState(false);
  
  // Define pageSizeOptions as a constant array of numbers (not strings)
  const pageSizeOptions = [5, 10, 20, 50, 100];

  useEffect(() => {
    const fetchPrograms = async () => {
      setLoading(true);
      try {
        const data = await getAllPrograms();
        setPrograms(data || []); // Add fallback to empty array
        setPaginationPrograms((prev) => ({ ...prev, total: data?.length || 0 }));
      } catch (error) {
        console.error("Error fetching programs:", error);
        setPrograms([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, []);

  const handleSearchPrograms = async () => {
    setLoading(true);
    try {
      const data = await getAllPrograms();
      const filtered = data.filter((program) => {
        const matchesName = program.programName
          ?.toLowerCase()
          .includes(searchProgramName.toLowerCase());
        const matchesDate = searchProgramDate
          ? moment(program.createdAt).isSame(searchProgramDate, "day")
          : true;
        return matchesName && matchesDate;
      });
      setPrograms(filtered || []); // Add fallback
      setPaginationPrograms((prev) => ({ 
        ...prev, 
        total: filtered?.length || 0, 
        current: 1 
      }));
    } catch (error) {
      console.error("Error searching programs:", error);
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setPaginationPrograms(pagination);
  };

  const columnsPrograms = [
    {
      title: "Program Name",
      dataIndex: "programName",
      key: "programName",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Duration (hours)",
      dataIndex: "duration",
      key: "duration",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Trainer",
      dataIndex: "trainer",
      key: "trainer",
      render: (trainer) => trainer?.full_name || "N/A",
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      render: (department) => department?.departmentName || "N/A",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (status ? "Active" : "Inactive"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => ( // Add underscore for unused text parameter
        <div className="action-table-data">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              setSelectedProgram(record);
              setOpenCreateProgramModal(true);
            }}
            title="Edit training program"
          >
            <i data-feather="edit" className="feather-edit"></i>
          </Link>
          <Popconfirm
            title="Deactivate the Training Program"
            description="Are you sure to deactivate this training program?"
            onConfirm={async () => {
              try {
                await deleteProgramById(record._id);
                await handleSearchPrograms();
              } catch (error) {
                console.error("Error deactivating program:", error);
              }
            }}
            okText="Yes"
            cancelText="No"
          >
            <Link className="confirm-text p-2" to="#" title="Deactivate program">
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container d-flex align-items-center">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>Training Programs</h4>
                <h6>Manage training programs</h6>
              </div>
            </div>
            <div className="page-btn">
              <button
                className="btn btn-added-grid"
                onClick={() => {
                  setSelectedProgram(null);
                  setOpenCreateProgramModal(true);
                }}
                title="Create new training program"
              >
                <PlusCircle className="me-2" />
                Program
              </button>
            </div>
          </div>
        </div>
        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: "100%", marginRight: "20px" }}>
            <div
              className="container mt-3"
              style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
            >
              <div className="row d-flex align-items-end">
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Program Name</label>
                  <input
                    type="text"
                    value={searchProgramName}
                    className="form-control"
                    onChange={(e) => setSearchProgramName(e.target.value)}
                    style={{ width: "100%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Created Date</label>
                  <DatePicker
                    className="form-control"
                    placeholder="Select date"
                    value={searchProgramDate}
                    onChange={(date) => setSearchProgramDate(date)}
                    style={{ width: "100%", height: "40px" }}
                    format="YYYY-MM-DD"
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <button
                    disabled={loading}
                    className="btn btn-submit"
                    onClick={handleSearchPrograms}
                    style={{ height: "40px", width: "100px" }}
                  >
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-top"></div>
            <div className="table-responsive">
              <Table
                loading={loading}
                columns={columnsPrograms}
                dataSource={programs}
                rowKey="_id"
                pagination={{
                  current: paginationPrograms.current,
                  pageSize: paginationPrograms.pageSize,
                  pageSizeOptions: pageSizeOptions, // Use the constant array
                  total: paginationPrograms.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }}
                onChange={handleTableChange}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        zIndex={200000}
        open={openCreateProgramModal}
        onCancel={() => setOpenCreateProgramModal(false)}
        footer={null}
        maskClosable={false}
        closable={false}
      >
        <CreateTrainingProgram
          selectedProgram={selectedProgram}
          handleCancel={() => {
            setSelectedProgram(null);
            setOpenCreateProgramModal(false);
          }}
          handleReload={handleSearchPrograms}
        />
      </Modal>
    </div>
  );
};

export default TrainingProgramList;